/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import ExcelJS from 'exceljs';
import { CertificateRecord, CertificateAttachment, CostCenter } from '../types.ts';
import { CORPORATE_LOGO_BASE64 } from './corporateLogoBase64.ts';

export interface CertificateExcelExportOptions {
  fileName?: string;
  reportTitle?: string;
  filterDescription?: string;
  costCenterName?: string;
  generatedBy?: string;
  includeExecutiveSummarySheet?: boolean;
  includeExpiringOnlySheet?: boolean;
  includeAttachmentsHistorySheet?: boolean;
  highlightExpiringDays?: number; // default 30 days
}

export interface CertificateValidityInfo {
  expiryDate: string;
  daysRemaining: number;
  computedStatus: 'vigente' | 'proximo_vencer' | 'vencido' | 'en_auditoria';
  statusLabel: string;
  priorityBadge: string;
  isUrgent30Days: boolean;
}

/**
 * Calculates real-time expiration metadata for a certification
 */
export function getCertificateValidityInfo(
  validUntilStr: string,
  rawStatus?: string,
  thresholdDays: number = 30
): CertificateValidityInfo {
  const expiryDateStr = validUntilStr || new Date().toISOString().split('T')[0];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDateStr);
  expiry.setHours(0, 0, 0, 0);

  const diffTime = expiry.getTime() - today.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  let computedStatus: 'vigente' | 'proximo_vencer' | 'vencido' | 'en_auditoria' = 'vigente';
  let statusLabel = 'VIGENTE';
  let priorityBadge = 'Normal';
  let isUrgent30Days = false;

  if (rawStatus === 'en_auditoria' || rawStatus === 'en_renovacion') {
    computedStatus = 'en_auditoria';
    statusLabel = 'EN AUDITORÍA / RENOVACIÓN';
    priorityBadge = 'Proceso Activo';
  } else if (daysRemaining < 0) {
    computedStatus = 'vencido';
    statusLabel = 'VENCIDA / EXPIRADA';
    priorityBadge = 'CRÍTICO: AUDITORÍA VENCIDA';
    isUrgent30Days = true;
  } else if (daysRemaining <= thresholdDays) {
    computedStatus = 'proximo_vencer';
    statusLabel = `POR VENCER (${daysRemaining} DÍAS)`;
    priorityBadge = `ALERTA 30 DÍAS (RENOVACIÓN)`;
    isUrgent30Days = true;
  } else {
    computedStatus = 'vigente';
    statusLabel = `VIGENTE (${daysRemaining} DÍAS)`;
    priorityBadge = 'Cumplimiento Pleno';
  }

  return {
    expiryDate: expiryDateStr,
    daysRemaining,
    computedStatus,
    statusLabel,
    priorityBadge,
    isUrgent30Days
  };
}

/**
 * Exports Certifications & Audits matrix to a high-end Excel workbook with corporate logo,
 * KPI blocks, 30-day alerts sheet, file attachments registry, and conditional formatting.
 */
export async function exportCertificatesToExcel(
  certificates: CertificateRecord[],
  options: CertificateExcelExportOptions = {}
): Promise<void> {
  const {
    fileName = `Matriz_Certificaciones_Auditorias_HSEQ_${new Date().toISOString().split('T')[0]}.xlsx`,
    reportTitle = 'MATRIZ DE CONTROL DE CERTIFICACIONES, AUDITORÍAS Y SISTEMAS DE GESTIÓN (HSEQ)',
    filterDescription = 'Todos los registros y auditorías evaluadas',
    costCenterName = 'Todas las Sedes Operativas / Plantas',
    generatedBy = 'Coordinación de Calidad, Seguridad y Medio Ambiente (HSEQ)',
    includeExecutiveSummarySheet = true,
    includeExpiringOnlySheet = true,
    includeAttachmentsHistorySheet = true,
    highlightExpiringDays = 30
  } = options;

  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Sistema de Gestión Integral HSEQ - Certificaciones & Auditorías';
  workbook.lastModifiedBy = generatedBy;
  workbook.created = new Date();
  workbook.modified = new Date();

  // Helper to add corporate logo
  const addLogoToSheet = (sheet: ExcelJS.Worksheet) => {
    try {
      if (CORPORATE_LOGO_BASE64 && CORPORATE_LOGO_BASE64.startsWith('data:image')) {
        const logoId = workbook.addImage({
          base64: CORPORATE_LOGO_BASE64,
          extension: 'jpeg'
        });
        sheet.addImage(logoId, {
          tl: { col: 0.2, row: 0.4 },
          ext: { width: 145, height: 48 },
          editAs: 'oneCell'
        });
      }
    } catch (e) {
      console.warn('[Certificates Excel Exporter] Could not add logo image:', e);
    }
  };

  // Enrich records with calculated validity info
  const enriched = certificates.map(c => ({
    ...c,
    validity: getCertificateValidityInfo(c.validUntil, c.status, highlightExpiringDays)
  }));

  // Summary Metrics
  const totalCerts = enriched.length;
  const expiringCount = enriched.filter(c => c.validity.daysRemaining >= 0 && c.validity.daysRemaining <= highlightExpiringDays && c.status !== 'en_auditoria').length;
  const expiredCount = enriched.filter(c => c.validity.daysRemaining < 0 && c.status !== 'en_auditoria').length;
  const inAuditCount = enriched.filter(c => c.validity.computedStatus === 'en_auditoria').length;
  const validCount = enriched.filter(c => c.validity.daysRemaining > highlightExpiringDays && c.validity.computedStatus === 'vigente').length;
  const totalFilesAttached = enriched.reduce((acc, c) => acc + (c.attachments?.length || 0), 0);

  // Colors Palette
  const NAVY_DARK = '0F172A';
  const SLATE_HEADER = '1E293B';
  const TEAL_ACCENT = '0D9488';
  const AMBER_EXPIRING = 'FEF3C7';
  const AMBER_TEXT = '92400E';
  const RED_EXPIRED = 'FEE2E2';
  const RED_TEXT = '991B1B';
  const GREEN_VALID = 'ECFDF5';
  const GREEN_TEXT = '065F46';
  const BLUE_AUDIT = 'EFF6FF';
  const BLUE_TEXT = '1E40AF';
  const BORDER_COLOR = 'CBD5E1';

  // ==========================================
  // SHEET 1: MATRIZ MAESTRA DE CERTIFICACIONES
  // ==========================================
  const wsMain = workbook.addWorksheet('Matriz Certificaciones HSEQ', {
    views: [{ state: 'frozen', ySplit: 8, xSplit: 0 }],
    pageSetup: { orientation: 'landscape', fitToWidth: 1, fitToHeight: 0, paperSize: 9 }
  });

  addLogoToSheet(wsMain);

  // Header Title Rows
  wsMain.mergeCells('C1:M1');
  const titleCell = wsMain.getCell('C1');
  titleCell.value = reportTitle;
  titleCell.font = { name: 'Arial', size: 13, bold: true, color: { argb: NAVY_DARK } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };

  wsMain.mergeCells('C2:M2');
  const subTitleCell = wsMain.getCell('C2');
  subTitleCell.value = `Sede / Centro de Costos: ${costCenterName} | Alcance: ${filterDescription}`;
  subTitleCell.font = { name: 'Arial', size: 10, italic: true, color: { argb: '475569' } };
  subTitleCell.alignment = { horizontal: 'left', vertical: 'middle' };

  wsMain.mergeCells('C3:M3');
  const metaCell = wsMain.getCell('C3');
  metaCell.value = `Emitido por: ${generatedBy} | Fecha de Emisión: ${new Date().toLocaleString('es-MX')} | Ventana de Alerta: ≤ ${highlightExpiringDays} días`;
  metaCell.font = { name: 'Arial', size: 9, color: { argb: '64748B' } };
  metaCell.alignment = { horizontal: 'left', vertical: 'middle' };

  // KPI Summary Cards Block (Row 5 & 6)
  const kpis = [
    { label: 'TOTAL CERTIFICACIONES', value: totalCerts, color: NAVY_DARK, bg: 'F1F5F9' },
    { label: 'VIGENTES (>30 DÍAS)', value: validCount, color: '065F46', bg: 'D1FAE5' },
    { label: `PRÓXIMAS A VENCER (≤${highlightExpiringDays}d)`, value: expiringCount, color: 'B45309', bg: 'FEF3C7' },
    { label: 'AUDITORÍAS VENCIDAS', value: expiredCount, color: '991B1B', bg: 'FEE2E2' },
    { label: 'EN AUDITORÍA / RENOVACIÓN', value: inAuditCount, color: '1E40AF', bg: 'DBEAFE' },
    { label: 'TOTAL ARCHIVOS ADJUNTOS', value: totalFilesAttached, color: '4338CA', bg: 'E0E7FF' }
  ];

  // Render KPI boxes across columns
  let kpiCol = 2; // Column B
  kpis.forEach((kpi) => {
    const topCell = wsMain.getRow(5).getCell(kpiCol);
    const bottomCell = wsMain.getRow(6).getCell(kpiCol);

    topCell.value = kpi.label;
    topCell.font = { name: 'Arial', size: 7.5, bold: true, color: { argb: kpi.color } };
    topCell.alignment = { horizontal: 'center', vertical: 'middle' };
    topCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.bg } };
    topCell.border = {
      top: { style: 'thin', color: { argb: BORDER_COLOR } },
      left: { style: 'thin', color: { argb: BORDER_COLOR } },
      right: { style: 'thin', color: { argb: BORDER_COLOR } }
    };

    bottomCell.value = kpi.value;
    bottomCell.font = { name: 'Arial', size: 14, bold: true, color: { argb: kpi.color } };
    bottomCell.alignment = { horizontal: 'center', vertical: 'middle' };
    bottomCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: kpi.bg } };
    bottomCell.border = {
      bottom: { style: 'medium', color: { argb: kpi.color } },
      left: { style: 'thin', color: { argb: BORDER_COLOR } },
      right: { style: 'thin', color: { argb: BORDER_COLOR } }
    };

    kpiCol += 2;
  });

  // Table Headers (Row 8)
  const headers = [
    '#',
    'CÓDIGO / NORMA',
    'NOMBRE DE LA CERTIFICACIÓN / AUDITORÍA',
    'CATEGORÍA',
    'ORGANISMO CERTIFICADOR / EMISOR',
    'SEDE / CENTRO DE COSTOS',
    'FECHA DE EMISIÓN',
    'VENCIMIENTO / AUDITORÍA LÍMITE',
    'DÍAS RESTANTES',
    'ESTATUS DE VIGENCIA',
    'ALERTA 30 DÍAS',
    'ARCHIVOS ADJUNTOS',
    'ALCANCE DE LA CERTIFICACIÓN',
    'AUDITOR LÍDER / RESPONSABLE',
    'TIPO DE AUDITORÍA'
  ];

  const headerRow = wsMain.getRow(8);
  headers.forEach((h, idx) => {
    const cell = headerRow.getCell(idx + 1);
    cell.value = h;
    cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY_DARK } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'medium', color: { argb: '000000' } },
      bottom: { style: 'medium', color: { argb: '000000' } },
      left: { style: 'thin', color: { argb: '334155' } },
      right: { style: 'thin', color: { argb: '334155' } }
    };
  });
  headerRow.height = 28;

  // Render Records
  let currentRowIdx = 9;
  enriched.forEach((cert, index) => {
    const row = wsMain.getRow(currentRowIdx);
    const validity = cert.validity;

    // Files summary
    const filesCount = cert.attachments?.length || 0;
    const fileTypes = cert.attachments ? Array.from(new Set(cert.attachments.map(a => a.fileType?.toUpperCase()))).join(', ') : 'Ninguno';
    const filesLabel = filesCount > 0 ? `${filesCount} archivo(s) [${fileTypes}]` : 'Sin archivos adjuntos';

    // Category Label
    const catLabels: Record<string, string> = {
      calidad: 'Calidad (ISO 9001 / IATF)',
      ambiental: 'Medio Ambiente (ISO 14001)',
      seguridad: 'Seguridad y Salud (ISO 45001 / STPS)',
      metrologia: 'Metrología y Calibración',
      proteccion_civil: 'Protección Civil / Bomberos',
      auditoria_cliente: 'Auditoría de Cliente (VDA / OEM)',
      inocuidad: 'Inocuidad y Sanidad',
      otro: 'Otra Acreditación'
    };
    const catFormatted = catLabels[cert.category] || cert.category || 'General';

    // Audit type
    const auditLabels: Record<string, string> = {
      certificacion: 'Certificación Inicial',
      seguimiento: 'Auditoría de Seguimiento / Vigilancia',
      recertificacion: 'Recertificación Periódica',
      interna: 'Auditoría Interna de Sistema',
      cliente: 'Auditoría Externa de Cliente'
    };
    const auditTypeFormatted = cert.auditType ? auditLabels[cert.auditType] || cert.auditType : 'Seguimiento Anual';

    row.getCell(1).value = index + 1;
    row.getCell(2).value = cert.code || 'S/C';
    row.getCell(3).value = cert.name;
    row.getCell(4).value = catFormatted;
    row.getCell(5).value = cert.issuer || 'Organismo Acreditado';
    row.getCell(6).value = cert.costCenterName || costCenterName;
    row.getCell(7).value = cert.issueDate || 'N/A';
    row.getCell(8).value = cert.validUntil || 'N/A';
    row.getCell(9).value = validity.daysRemaining;
    row.getCell(10).value = validity.statusLabel;
    row.getCell(11).value = validity.isUrgent30Days 
      ? (validity.daysRemaining < 0 ? 'CRÍTICO: VENCIDO' : `ALERTA RENOVACIÓN (≤${highlightExpiringDays}d)`) 
      : 'VIGENTE';
    row.getCell(12).value = filesLabel;
    row.getCell(13).value = cert.scope || 'Sin alcance detallado';
    row.getCell(14).value = cert.leadAuditor || 'Por designar';
    row.getCell(15).value = auditTypeFormatted;

    // Formatting & Colors
    let rowBg = 'FFFFFF';
    let textColor = '1E293B';
    let boldText = false;

    if (validity.computedStatus === 'vencido') {
      rowBg = RED_EXPIRED;
      textColor = RED_TEXT;
      boldText = true;
    } else if (validity.computedStatus === 'proximo_vencer') {
      rowBg = AMBER_EXPIRING;
      textColor = AMBER_TEXT;
      boldText = true;
    } else if (validity.computedStatus === 'en_auditoria') {
      rowBg = BLUE_AUDIT;
      textColor = BLUE_TEXT;
    } else {
      rowBg = index % 2 === 0 ? 'F8FAFC' : 'FFFFFF';
    }

    for (let c = 1; c <= 15; c++) {
      const cell = row.getCell(c);
      cell.font = { name: 'Arial', size: 8.5, color: { argb: textColor }, bold: boldText && (c === 2 || c === 3 || c === 9 || c === 10 || c === 11) };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
      cell.border = {
        top: { style: 'thin', color: { argb: BORDER_COLOR } },
        bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
        left: { style: 'thin', color: { argb: BORDER_COLOR } },
        right: { style: 'thin', color: { argb: BORDER_COLOR } }
      };

      // Alignment rules
      if (c === 1 || c === 7 || c === 8 || c === 9 || c === 10 || c === 11) {
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      } else {
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
      }
    }

    row.height = 22;
    currentRowIdx++;
  });

  // If no rows
  if (enriched.length === 0) {
    const emptyRow = wsMain.getRow(currentRowIdx);
    wsMain.mergeCells(`A${currentRowIdx}:O${currentRowIdx}`);
    const emptyCell = emptyRow.getCell(1);
    emptyCell.value = 'No se encontraron certificaciones ni auditorías que cumplan con los criterios de filtrado.';
    emptyCell.font = { name: 'Arial', size: 10, italic: true, color: { argb: '64748B' } };
    emptyCell.alignment = { horizontal: 'center', vertical: 'middle' };
    emptyRow.height = 30;
    currentRowIdx++;
  }

  // Signatures Section (at bottom)
  currentRowIdx += 2;
  const signTitleRow = wsMain.getRow(currentRowIdx);
  signTitleRow.getCell(2).value = 'VALIDACIÓN INSTITUCIONAL DE AUDITORÍAS Y SISTEMAS DE GESTIÓN (HSEQ):';
  signTitleRow.getCell(2).font = { name: 'Arial', size: 9, bold: true, color: { argb: NAVY_DARK } };
  currentRowIdx += 3;

  // Signature lines
  const sigRow1 = wsMain.getRow(currentRowIdx);
  sigRow1.getCell(2).value = '_______________________________________';
  sigRow1.getCell(6).value = '_______________________________________';
  sigRow1.getCell(11).value = '_______________________________________';
  for (let c of [2, 6, 11]) {
    sigRow1.getCell(c).alignment = { horizontal: 'center' };
    sigRow1.getCell(c).font = { name: 'Arial', size: 9, color: { argb: '64748B' } };
  }

  currentRowIdx++;
  const sigRow2 = wsMain.getRow(currentRowIdx);
  sigRow2.getCell(2).value = 'Coordinador de Calidad & HSEQ';
  sigRow2.getCell(6).value = 'Auditor Líder / Enlace STPS';
  sigRow2.getCell(11).value = 'Gerencia de Planta / Dirección General';
  for (let c of [2, 6, 11]) {
    sigRow2.getCell(c).alignment = { horizontal: 'center' };
    sigRow2.getCell(c).font = { name: 'Arial', size: 9, bold: true, color: { argb: NAVY_DARK } };
  }

  // Set Column Widths for Sheet 1
  wsMain.getColumn(1).width = 6;   // #
  wsMain.getColumn(2).width = 16;  // Código
  wsMain.getColumn(3).width = 38;  // Nombre
  wsMain.getColumn(4).width = 24;  // Categoría
  wsMain.getColumn(5).width = 25;  // Ente Emisor
  wsMain.getColumn(6).width = 22;  // Planta
  wsMain.getColumn(7).width = 14;  // Emisión
  wsMain.getColumn(8).width = 16;  // Vencimiento
  wsMain.getColumn(9).width = 12;  // Días Restantes
  wsMain.getColumn(10).width = 22; // Estatus
  wsMain.getColumn(11).width = 24; // Alerta 30d
  wsMain.getColumn(12).width = 24; // Archivos
  wsMain.getColumn(13).width = 36; // Alcance
  wsMain.getColumn(14).width = 24; // Auditor
  wsMain.getColumn(15).width = 24; // Tipo Auditoría

  // =========================================================================
  // SHEET 2: HOJA EXCLUSIVA DE ALERTA DE VENCIMIENTO (30 DÍAS / RENOVACIÓN)
  // =========================================================================
  if (includeExpiringOnlySheet) {
    const wsExp = workbook.addWorksheet(`Alertas Renovación (${highlightExpiringDays}d)`, {
      views: [{ state: 'frozen', ySplit: 7, xSplit: 0 }],
      pageSetup: { orientation: 'landscape', fitToWidth: 1, fitToHeight: 0, paperSize: 9 }
    });

    addLogoToSheet(wsExp);

    wsExp.mergeCells('C1:K1');
    const tExp = wsExp.getCell('C1');
    tExp.value = `PLAN DE ACCIÓN Y ALERTAS CRÍTICAS: CERTIFICACIONES Y AUDITORÍAS (≤ ${highlightExpiringDays} DÍAS)`;
    tExp.font = { name: 'Arial', size: 12, bold: true, color: { argb: 'B45309' } };
    tExp.alignment = { horizontal: 'left', vertical: 'middle' };

    wsExp.mergeCells('C2:K2');
    const stExp = wsExp.getCell('C2');
    stExp.value = `Monitoreo preventivo para gestión oportuna de recertificación, trámites ante organismos certificadores y evidencias.`;
    stExp.font = { name: 'Arial', size: 9.5, italic: true, color: { argb: '475569' } };

    // Filter only expiring soon & expired
    const urgentCerts = enriched.filter(c => c.validity.daysRemaining <= highlightExpiringDays);

    // Headers
    const expHeaders = [
      '#',
      'CÓDIGO / NORMA',
      'CERTIFICACIÓN / AUDITORÍA',
      'ORGANISMO CERTIFICADOR',
      'SEDE OPERATIVA',
      'FECHA LÍMITE',
      'DÍAS RESTANTES',
      'NIVEL DE URGENCIA',
      'ARCHIVOS / EVIDENCIAS',
      'AUDITOR LÍDER',
      'ACCIONES RECOMENDADAS PARA RENOVACIÓN'
    ];

    const expHeaderRow = wsExp.getRow(7);
    expHeaders.forEach((h, idx) => {
      const cell = expHeaderRow.getCell(idx + 1);
      cell.value = h;
      cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'B45309' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'medium', color: { argb: '000000' } },
        bottom: { style: 'medium', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: 'D97706' } },
        right: { style: 'thin', color: { argb: 'D97706' } }
      };
    });
    expHeaderRow.height = 26;

    let expRowIdx = 8;
    urgentCerts.forEach((cert, idx) => {
      const row = wsExp.getRow(expRowIdx);
      const v = cert.validity;

      let actionDesc = 'Programar auditoría de seguimiento con el organismo acreditado.';
      if (v.daysRemaining < 0) {
        actionDesc = 'URGENTE: Iniciar trámite de recertificación y solicitar carta de prórroga / auditoría extraordinaria.';
      } else if (v.daysRemaining <= 15) {
        actionDesc = 'CRÍTICO: Validar cierre de no conformidades previas y enviar paquete documental al auditor.';
      } else if (v.daysRemaining <= 30) {
        actionDesc = 'ALERTA 30 DÍAS: Realizar pre-auditoría interna HSEQ y confirmar fecha con el ente certificador.';
      }

      const filesCount = cert.attachments?.length || 0;

      row.getCell(1).value = idx + 1;
      row.getCell(2).value = cert.code;
      row.getCell(3).value = cert.name;
      row.getCell(4).value = cert.issuer;
      row.getCell(5).value = cert.costCenterName || costCenterName;
      row.getCell(6).value = cert.validUntil;
      row.getCell(7).value = v.daysRemaining;
      row.getCell(8).value = v.priorityBadge;
      row.getCell(9).value = `${filesCount} archivo(s) disponibles`;
      row.getCell(10).value = cert.leadAuditor || 'Por designar';
      row.getCell(11).value = actionDesc;

      const isExp = v.daysRemaining < 0;
      const rowBg = isExp ? RED_EXPIRED : AMBER_EXPIRING;
      const txtColor = isExp ? RED_TEXT : AMBER_TEXT;

      for (let c = 1; c <= 11; c++) {
        const cell = row.getCell(c);
        cell.font = { name: 'Arial', size: 8.5, color: { argb: txtColor }, bold: c === 2 || c === 3 || c === 7 || c === 8 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } };
        cell.border = {
          top: { style: 'thin', color: { argb: BORDER_COLOR } },
          bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
          left: { style: 'thin', color: { argb: BORDER_COLOR } },
          right: { style: 'thin', color: { argb: BORDER_COLOR } }
        };
        if (c === 1 || c === 6 || c === 7 || c === 8) {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        }
      }

      row.height = 24;
      expRowIdx++;
    });

    if (urgentCerts.length === 0) {
      const row = wsExp.getRow(expRowIdx);
      wsExp.mergeCells(`A${expRowIdx}:K${expRowIdx}`);
      row.getCell(1).value = `Excelente: No hay certificaciones ni auditorías próximas a vencer en la ventana de ${highlightExpiringDays} días.`;
      row.getCell(1).font = { name: 'Arial', size: 10, italic: true, color: { argb: '065F46' } };
      row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
      row.height = 28;
    }

    // Column widths for sheet 2
    wsExp.getColumn(1).width = 6;
    wsExp.getColumn(2).width = 16;
    wsExp.getColumn(3).width = 36;
    wsExp.getColumn(4).width = 24;
    wsExp.getColumn(5).width = 22;
    wsExp.getColumn(6).width = 16;
    wsExp.getColumn(7).width = 14;
    wsExp.getColumn(8).width = 24;
    wsExp.getColumn(9).width = 20;
    wsExp.getColumn(10).width = 22;
    wsExp.getColumn(11).width = 44;
  }

  // =========================================================================
  // SHEET 3: CATÁLOGO CENTRAL E HISTÓRICO DE ARCHIVOS Y EVIDENCIAS ADJUNTAS
  // =========================================================================
  if (includeAttachmentsHistorySheet) {
    const wsFiles = workbook.addWorksheet('Histórico de Archivos Adjuntos', {
      views: [{ state: 'frozen', ySplit: 6, xSplit: 0 }],
      pageSetup: { orientation: 'landscape', fitToWidth: 1, fitToHeight: 0, paperSize: 9 }
    });

    addLogoToSheet(wsFiles);

    wsFiles.mergeCells('C1:I1');
    const tFiles = wsFiles.getCell('C1');
    tFiles.value = 'EXPEDIENTE DIGITAL E HISTÓRICO DE DOCUMENTOS Y EVIDENCIAS HSEQ';
    tFiles.font = { name: 'Arial', size: 12, bold: true, color: { argb: '4338CA' } };
    tFiles.alignment = { horizontal: 'left', vertical: 'middle' };

    wsFiles.mergeCells('C2:I2');
    const stFiles = wsFiles.getCell('C2');
    stFiles.value = 'Registro detallado de archivos adjuntos (PDF, Word, PowerPoint, Imágenes) y versiones históricas de auditorías.';
    stFiles.font = { name: 'Arial', size: 9.5, italic: true, color: { argb: '475569' } };

    const fileHeaders = [
      '#',
      'NORMA / CÓDIGO',
      'CERTIFICACIÓN ASOCIADA',
      'NOMBRE DEL DOCUMENTO',
      'TIPO DE ARCHIVO',
      'TAMAÑO ESTIMADO',
      'FECHA DE SUBIDA',
      'SUBIDO POR / AUDITOR',
      'VERSIÓN / NOTAS'
    ];

    const fHeaderRow = wsFiles.getRow(6);
    fileHeaders.forEach((h, idx) => {
      const cell = fHeaderRow.getCell(idx + 1);
      cell.value = h;
      cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4338CA' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'medium', color: { argb: '000000' } },
        bottom: { style: 'medium', color: { argb: '000000' } },
        left: { style: 'thin', color: { argb: '6366F1' } },
        right: { style: 'thin', color: { argb: '6366F1' } }
      };
    });
    fHeaderRow.height = 25;

    let fRowIdx = 7;
    let fileCounter = 1;

    enriched.forEach(cert => {
      if (cert.attachments && cert.attachments.length > 0) {
        cert.attachments.forEach(att => {
          const row = wsFiles.getRow(fRowIdx);

          row.getCell(1).value = fileCounter++;
          row.getCell(2).value = cert.code;
          row.getCell(3).value = cert.name;
          row.getCell(4).value = att.name;
          row.getCell(5).value = att.fileType?.toUpperCase() || 'DOCUMENTO';
          row.getCell(6).value = att.sizeFormatted || 'N/A';
          row.getCell(7).value = att.uploadedAt ? att.uploadedAt.split('T')[0] : 'Reciente';
          row.getCell(8).value = att.uploadedBy || 'Auditor HSEQ';
          row.getCell(9).value = att.notes || (att.versionNumber ? `Versión ${att.versionNumber}` : 'Documento Vigente');

          for (let c = 1; c <= 9; c++) {
            const cell = row.getCell(c);
            cell.font = { name: 'Arial', size: 8.5, color: { argb: '1E293B' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fRowIdx % 2 === 0 ? 'F8FAFC' : 'FFFFFF' } };
            cell.border = {
              top: { style: 'thin', color: { argb: BORDER_COLOR } },
              bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
              left: { style: 'thin', color: { argb: BORDER_COLOR } },
              right: { style: 'thin', color: { argb: BORDER_COLOR } }
            };
            if (c === 1 || c === 2 || c === 5 || c === 6 || c === 7) {
              cell.alignment = { horizontal: 'center', vertical: 'middle' };
            } else {
              cell.alignment = { horizontal: 'left', vertical: 'middle' };
            }
          }

          row.height = 20;
          fRowIdx++;
        });
      }
    });

    if (fileCounter === 1) {
      const row = wsFiles.getRow(fRowIdx);
      wsFiles.mergeCells(`A${fRowIdx}:I${fRowIdx}`);
      row.getCell(1).value = 'No se registraron archivos adjuntos en las certificaciones evaluadas.';
      row.getCell(1).font = { name: 'Arial', size: 9.5, italic: true, color: { argb: '64748B' } };
      row.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
      row.height = 26;
    }

    wsFiles.getColumn(1).width = 6;
    wsFiles.getColumn(2).width = 16;
    wsFiles.getColumn(3).width = 34;
    wsFiles.getColumn(4).width = 32;
    wsFiles.getColumn(5).width = 16;
    wsFiles.getColumn(6).width = 16;
    wsFiles.getColumn(7).width = 16;
    wsFiles.getColumn(8).width = 22;
    wsFiles.getColumn(9).width = 30;
  }

  // =========================================================================
  // GENERATE AND TRIGGER DOWNLOAD
  // =========================================================================
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}
